import { FC, useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { PrivacyPDF, TermsPDF } from "@ryde/assets-landings";
import {
  CheckboxControlledMui,
  MessagesDisplayContainer,
  PhoneTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import {
  apiDataFetchingLandingAtom,
  HerzliyaLoginStepFormFieldsEnum,
  landingMessagesAtom,
} from "@ryde/utils-landings";

import { ContinueButton } from "../../widgets";
import { useStepLoginHerzliya } from "./use-step-login.herzliya";

export const StepLoginHerzliya: FC = () => {
  const { methods, handleLogin, isContinueCanBeApply } = useStepLoginHerzliya();
  const [searchParams, setSearchParams] = useSearchParams();
  const landingMessages = useRecoilValue(landingMessagesAtom);
  const isApiLoading = useRecoilValue(apiDataFetchingLandingAtom);
  const { t } = useTranslation();

  const { watch, setValue } = methods;

  useEffect(() => {
    if (searchParams) {
      setSearchParams("");
    }
  }, [searchParams, setSearchParams]);

  const acceptTermsWatchField = watch(
    HerzliyaLoginStepFormFieldsEnum.AcceptTerms,
  );

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={e => e.preventDefault()}>
        <h1 className="landing-title">
          {t("landing.login.title")}
          <br />
          {t("landing.login.subTitle")}
        </h1>

        <div className="landing-form-field">
          <label htmlFor={HerzliyaLoginStepFormFieldsEnum.FirstName}>
            {t("landing.formLabel.firstName")}*
          </label>
          <TextboxTemplate
            uniqueAttrId={HerzliyaLoginStepFormFieldsEnum.FirstName}
            name={HerzliyaLoginStepFormFieldsEnum.FirstName}
          />
        </div>

        <div className="landing-form-field">
          <label htmlFor={HerzliyaLoginStepFormFieldsEnum.lastName}>
            {t("landing.formLabel.lastName")}*
          </label>
          <TextboxTemplate
            uniqueAttrId={HerzliyaLoginStepFormFieldsEnum.lastName}
            name={HerzliyaLoginStepFormFieldsEnum.lastName}
          />
        </div>

        <div className="landing-form-field">
          <label htmlFor={HerzliyaLoginStepFormFieldsEnum.Phone}>
            {t("landing.formLabel.phone")}*
          </label>
          <PhoneTemplate
            uniqueAttrId={HerzliyaLoginStepFormFieldsEnum.Phone}
            name={HerzliyaLoginStepFormFieldsEnum.Phone}
            sx={{ size: "small" }}
            formAttributes={{ disablePhoneCallable: true }}
          />
        </div>

        <div className="landing-form-field">
          <div className="landing-accept-terms">
            <CheckboxControlledMui
              uniqueAttrId={HerzliyaLoginStepFormFieldsEnum.AcceptTerms}
              width={16}
              height={16}
              checked={acceptTermsWatchField ?? false}
              setCheckedToggle={() =>
                setValue(
                  HerzliyaLoginStepFormFieldsEnum.AcceptTerms,
                  !acceptTermsWatchField,
                )
              }
            />
            <span className="custom-label">
              <Trans
                i18nKey="landing.formLabel.privacy_notice"
                components={{
                  secure_link: (
                    <a
                      aria-label="Go to privacy notice"
                      href={PrivacyPDF}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  br: <br />,
                }}
              />
            </span>
          </div>
        </div>

        <MessagesDisplayContainer
          serverMessages={landingMessages?.messages}
          type={landingMessages?.type}
        />

        <ContinueButton
          loading={isApiLoading}
          disabled={!isContinueCanBeApply}
          onClick={handleLogin}
        />

        <div className="landing-bottom-illustration" aria-hidden="true"></div>

        <div className="landing-form-field">
          <span className="text-center custom-label">
            <Trans
              i18nKey="landing.formLabel.security_certification"
              components={{
                strong: <strong />,
                br: <br />,
                secure_link: (
                  <a
                    aria-label="Go to security certification"
                    href={TermsPDF}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </span>
        </div>

        <div className="landing-container-footer">
          <div
            className="landing-container-footer-logo"
            aria-hidden="true"
          ></div>
        </div>
      </form>
    </FormProvider>
  );
};
